import * as React from "react";
import { SignatureData } from "intake_shared/src/types";
import { cn } from "../../../../helpers";
import { FormCheckbox } from "../../../bits/FormCheckbox/FormCheckbox";
import { Input } from "../../../bits/Input/Input";
import styles from "./SignatureForm.module.scss";
import { CacheContext } from "../../../../../contexts/CacheContext";
import { AclContext } from "../../../../../contexts/AclContext";
import { useToasts } from "react-toast-notifications";
import { v4 as uuidv4 } from "uuid";
import { addNewEmailSignature } from "../../../../../store/email-signatures";
import { updateEmailSignatureCache } from "../../../../../store/email-signatures";
import { Button } from "../../../bits/Button/Button";

interface SignatureFormProps {
    initialData: SignatureData;
    mode: "create" | "edit";
    setCurrentSignature: (value: SignatureData) => void;
    setEditSignature?: (val: boolean) => void;
}

export default function SignatureForm({
    initialData,
    mode,
    setCurrentSignature,
    setEditSignature,
}: SignatureFormProps) {
    const olc = React.useContext(CacheContext);
    const userAcl = React.useContext(AclContext)?.authUser?.acl;
    const [formData, setFormData] = React.useState<SignatureData>(initialData);
    const { addToast } = useToasts();

    React.useEffect(() => {
        setFormData(initialData);
    }, [initialData]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;

        if (type === "checkbox") {
            setFormData({
                ...formData,
                [name]: checked,
            });
            setCurrentSignature({
                ...formData,
                [name]: checked,
            });
        } else {
            setCurrentSignature({
                ...formData,
                [name]: value,
            });
            setFormData({
                ...formData,
                [name]: value,
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.name || !formData.email) {
            addToast("Name and email are required", {
                appearance: "error",
            });
            return;
        }
        const newSignature: SignatureData = {
            ...formData,
            id: uuidv4(),
        };

        addNewEmailSignature(newSignature);
        const returnedEmailSignatures = await updateEmailSignatureCache();
        if (returnedEmailSignatures) {
            olc?.updateEmailSignatures(returnedEmailSignatures);
        }
        addToast("Your email signature has been saved", {
            appearance: "success",
        });
        if (setEditSignature) {
            setEditSignature(false);
        }
        setFormData({
            id: "",
            name: "",
            pronouns: "",
            email: "",
            company: "",
            department: "",
            phone: "",
            directPhone: "",
            includeDisclaimer: true,
        });
        setCurrentSignature({
            id: "",
            name: "",
            pronouns: "",
            email: "",
            company: "",
            department: "",
            phone: "",
            directPhone: "",
            includeDisclaimer: true,
        });
    };
    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <p className={styles.title}>Email Signature Information</p>
                <p className={styles.description}>
                    Enter your information to generate a signature
                </p>
            </div>
            <form onSubmit={handleSubmit}>
                <div className={cn("v2-form", styles.form)}>
                    <div className="gridTwo">
                        <Input
                            isRequired
                            label="Full Name"
                            placeholder="Enter full name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                        />
                        <Input
                            isRequired
                            label="Pronouns"
                            placeholder="Enter your pronouns"
                            name="pronouns"
                            value={formData.pronouns}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="gridTwo">
                        <Input
                            isRequired
                            label="Email Address"
                            placeholder="Enter email address"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={!userAcl?.super_admin || mode === "edit"}
                        />
                        <Input
                            label="Company"
                            placeholder="Enter company name"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="gridTwo">
                        <Input
                            label="Department/Title"
                            placeholder="Enter department/title"
                            name="department"
                            value={formData.department}
                            onChange={handleChange}
                        />
                        <Input
                            label="Office Phone Number"
                            placeholder="Enter office phone number"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="gridTwo">
                        <Input
                            label="Direct/Mobile Number"
                            placeholder="Enter direct/mobile phone number"
                            name="directPhone"
                            value={formData.directPhone}
                            onChange={handleChange}
                        />
                        <FormCheckbox
                            inForm
                            label="Include legal disclaimer"
                            name="includeDisclaimer"
                            checked={formData.includeDisclaimer}
                            onChange={handleChange}
                        />
                    </div>
                </div>
                <div className={styles.saveButtonContainer}>
                    <Button
                        type="submit"
                        variant="primary"
                        className={styles.saveButton}
                    >
                        Save Signature
                    </Button>
                </div>
            </form>
        </div>
    );
}
